import posthog from 'posthog-js';

export interface PostHogEventProperties {
  [key: string]: any;
}

/**
 * Check if PostHog is properly loaded and initialized
 */
function isPostHogAvailable(): boolean {
  try {
    return !!(posthog && typeof posthog.capture === 'function' && posthog.__loaded);
  } catch (error) {
    console.warn('[PostHog] Error checking PostHog availability:', error);
    return false;
  }
}

/**
 * PostHog Analytics Service
 * Centralized service for tracking custom events and user behavior
 */
export class PostHogService {
  /**
   * Track a custom event
   */
  static capture(eventName: string, properties?: PostHogEventProperties): void {
    try {
      if (isPostHogAvailable()) {
        posthog.capture(eventName, properties);
        console.log(`[PostHog] Event tracked: ${eventName}`, properties);
      } else {
        console.warn('[PostHog] PostHog not loaded, event not tracked:', eventName);
        // In development, we might want to track this differently
        if (import.meta.env.DEV) {
          console.log(`[PostHog] Would track: ${eventName}`, properties);
        }
      }
    } catch (error) {
      console.error('[PostHog] Error tracking event:', eventName, error);
    }
  }

  /**
   * Identify a user
   */
  static identify(userId: string, userProperties?: PostHogEventProperties): void {
    try {
      if (isPostHogAvailable()) {
        posthog.identify(userId, userProperties);
        console.log(`[PostHog] User identified: ${userId}`, userProperties);
      } else {
        console.warn('[PostHog] PostHog not loaded, cannot identify user:', userId);
      }
    } catch (error) {
      console.error('[PostHog] Error identifying user:', userId, error);
    }
  }

  /**
   * Track page views manually (if needed beyond automatic tracking)
   */
  static trackPageView(pageName: string, properties?: PostHogEventProperties): void {
    this.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    });
  }

  /**
   * Track button clicks
   */
  static trackButtonClick(buttonName: string, location?: string, additionalProperties?: PostHogEventProperties): void {
    this.capture('button_click', {
      button_name: buttonName,
      location: location || window.location.pathname,
      ...additionalProperties,
    });
  }

  /**
   * Track scan events (specific to SafeVibe)
   */
  static trackScanEvent(action: 'started' | 'completed' | 'failed', scanType?: string, properties?: PostHogEventProperties): void {
    this.capture(`scan_${action}`, {
      scan_type: scanType,
      ...properties,
    });
  }

  /**
   * Track user registration
   */
  static trackSignup(method: string, properties?: PostHogEventProperties): void {
    this.capture('signup_click', {
      method,
      location: window.location.pathname,
      ...properties,
    });
  }

  /**
   * Track user authentication
   */
  static trackLogin(method: string, properties?: PostHogEventProperties): void {
    this.capture('login_success', {
      method,
      ...properties,
    });
  }

  /**
   * Track feature usage (for feature flagging insights)
   */
  static trackFeatureUsage(featureName: string, properties?: PostHogEventProperties): void {
    this.capture('feature_used', {
      feature_name: featureName,
      ...properties,
    });
  }

  /**
   * Check if PostHog is available for use
   */
  static isAvailable(): boolean {
    return isPostHogAvailable();
  }

  /**
   * Get PostHog status for debugging
   */
  static getStatus(): { available: boolean; loaded: boolean; hasCapture: boolean } {
    return {
      available: isPostHogAvailable(),
      loaded: !!(posthog && posthog.__loaded),
      hasCapture: !!(posthog && typeof posthog.capture === 'function'),
    };
  }
}

// Export the class as default and also provide named exports for convenience
export default PostHogService;

// Convenience exports
export const {
  capture,
  identify,
  trackPageView,
  trackButtonClick,
  trackScanEvent,
  trackSignup,
  trackLogin,
  trackFeatureUsage,
  isAvailable,
  getStatus,
} = PostHogService; 